/**
 * How authored text names a tested asset: the one owner the server (the Save,
 * the compiler) and the builder's mention field both call.
 *
 * - Identity (`buildAssetMentionBindingTable`, `bindAssetMentionTokens`): an
 *   explicit `@` mention is the token of the asset it names (`assetRefToken`,
 *   by stable key; D2 S3.1). It never interprets an unprefixed headline, name
 *   or ordinary answer label as a stimulus reference.
 * - The shared-name rule (`assetMentionLabel`): what a mention several
 *   assets answer to reads as.
 * - What a rename does to mentions (`assetRenames`).
 *
 * Cache scope: one-shot per caller. Tables are small and rebuilt from the
 * current catalog; nothing here is an LLM prompt prefix.
 */

import {
  ASSET_MENTION_TRAILING_LOOKAHEAD,
  assetMentionAliasPattern,
  buildAssetMentionAliases,
} from "./asset-mention-aliases";
import { assetRefToken } from "./asset-ref-tokens";

function cleanText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export interface AssetMentionBindingTable {
  canonicalByAlias: Map<string, string>;
  nameByAssetId: Map<string, string>;
  exactAliases: Array<{ alias: string; canonical: string }>;
  /**
   * Each name's assets by stable key, in catalog order. Price versions of one
   * product share its name, and a mention of the name names them all (owner
   * decision A): its token is the first version's, and the readers take the
   * others with it, unless the text is an answer that is one version itself.
   */
  keysByName: Map<string, Array<string>>;
  keyByAssetId: Map<string, string>;
}

function extraIdentityAliases(asset: Record<string, unknown>): Array<string> {
  const extras: Array<string> = [];
  for (const value of [
    asset.headline,
    asset.messageText,
    asset.proposition,
    asset.twinFacingLabel,
  ]) {
    const text = cleanText(value);
    if (text) {
      extras.push(text);
    }
  }
  return extras;
}

/** The alias table a catalog's mentions bind against. */
export function buildAssetMentionBindingTable(
  testedAssets: { assets?: Array<any> } | null | undefined,
): AssetMentionBindingTable {
  const canonicalByAlias = new Map<string, string>();
  const nameByAssetId = new Map<string, string>();
  const keysByName = new Map<string, Array<string>>();
  const keyByAssetId = new Map<string, string>();
  const ambiguous = new Set<string>();
  const assets = Array.isArray(testedAssets?.assets) ? testedAssets.assets : [];

  for (const asset of assets) {
    const name = cleanText(asset?.name);
    if (!name) {
      continue;
    }
    if (asset?.id) {
      nameByAssetId.set(String(asset.id), name);
    }
    const stableKey = cleanText(asset?.stableKey);
    if (stableKey) {
      keysByName.set(name.toLowerCase(), [
        ...(keysByName.get(name.toLowerCase()) ?? []),
        stableKey,
      ]);
      if (asset?.id) keyByAssetId.set(String(asset.id), stableKey);
    }
    const aliases = [
      ...buildAssetMentionAliases({
        name,
        type: String(asset.type ?? ""),
        id: asset.id,
        stableKey: asset.stableKey,
      }),
      ...extraIdentityAliases(asset),
    ];
    const seen = new Set<string>();
    for (const alias of aliases) {
      const key = alias.toLowerCase();
      if (!alias || seen.has(key)) {
        continue;
      }
      seen.add(key);
      if (ambiguous.has(key)) {
        continue;
      }
      const existing = canonicalByAlias.get(key);
      if (existing && existing !== name) {
        canonicalByAlias.delete(key);
        ambiguous.add(key);
        continue;
      }
      canonicalByAlias.set(key, name);
    }
  }

  const exactAliases = [...canonicalByAlias.entries()]
    .map(([key, canonical]) => ({ alias: key, canonical }))
    .sort((a, b) => b.alias.length - a.alias.length);
  return {
    canonicalByAlias,
    nameByAssetId,
    exactAliases,
    keysByName,
    keyByAssetId,
  };
}

/**
 * The token of the asset `alias` names, if it names one: `ownKey`'s when that
 * asset carries the name (an answer that is one price version), else the
 * first asset's that carries it.
 */
function tokenOfAlias(
  alias: string,
  tables: AssetMentionBindingTable,
  ownKey?: string,
): string | undefined {
  const canonical = tables.canonicalByAlias.get(alias.toLowerCase());
  const keys = canonical ? tables.keysByName.get(canonical.toLowerCase()) : undefined;
  if (!keys || keys.length === 0) return undefined;
  return assetRefToken(ownKey && keys.includes(ownKey) ? ownKey : keys[0]);
}

/**
 * `value` with each explicit `@` mention written as the token of the asset it
 * names. Text that already holds tokens keeps them.
 */
export function bindAssetMentionTokens(
  value: unknown,
  tables: AssetMentionBindingTable,
  ownKey?: string,
): string {
  const text = typeof value === "string" ? value : "";
  if (!text) {
    return text;
  }
  let next = text;

  // Each @mention becomes its asset's token (including Page N / Ad N
  // ordinals), longest alias first. A token holds no `@`, so a shorter alias
  // can never match inside a mention a longer alias already took.
  const exactLongestFirst = [...tables.exactAliases].sort(
    (a, b) => b.alias.length - a.alias.length,
  );
  const allAliases = exactLongestFirst.map((entry) => entry.alias);
  for (const { alias } of exactLongestFirst) {
    const token = tokenOfAlias(alias, tables, ownKey);
    if (!token) continue;
    const pattern = new RegExp(
      `(^|[^@A-Za-z0-9_])@\\s*(${assetMentionAliasPattern(alias, allAliases)})${ASSET_MENTION_TRAILING_LOOKAHEAD}`,
      "gi",
    );
    next = next.replace(pattern, (_match, prefix) => `${prefix}${token}`);
  }
  return next;
}

/**
 * What a mention several assets answer to reads as (the shared-name rule):
 * the text's own asset's label when it is one of them (an answer that is one
 * price version), else their label when they share one, else the first one's
 * name (owner decision A, 2026-09-25).
 */
export function assetMentionLabel<T extends { id: string; name: string }>(
  holders: ReadonlyArray<T>,
  own: ReadonlySet<string> | undefined,
  labelOf: (holder: T) => string,
  ownLabelOf: (holder: T) => string,
): string {
  const mine = own ? holders.find((holder) => own.has(holder.id)) : undefined;
  if (mine) return ownLabelOf(mine);
  const labels = new Set(holders.map((holder) => labelOf(holder)));
  return labels.size === 1 ? labelOf(holders[0]) : holders[0].name;
}

/** One asset's rename between two catalogs, and what its mentions do. */
export interface AssetRename {
  id: string;
  stableKey?: string;
  from: string;
  to: string;
  /**
   * - `follows`: a mention of it names the renamed asset, under its new name.
   * - `{ keeper }`: price versions shared the old name and `keeper` kept it,
   *   so a mention of the name keeps naming the versions that kept it (owner
   *   decision A); only an answer that is the renamed version follows it.
   * - `stranded`: every version sharing the old name was renamed apart, so
   *   the text alone cannot choose which one it meant and keeps its words.
   */
  mention: "follows" | { keeper: string } | "stranded";
}

/**
 * The assets whose name differs between two catalogs, matched by identity
 * (`id`, else `stableKey`), never by position.
 */
export function assetRenames(
  prior: ReadonlyArray<{ id?: unknown; stableKey?: unknown; name?: unknown }>,
  next: ReadonlyArray<{ id?: unknown; stableKey?: unknown; name?: unknown }>,
): Array<AssetRename> {
  const priorNameByIdentity = new Map<string, string>();
  // The assets that carried each name before (the price versions of one
  // product share it).
  const priorKeysByName = new Map<string, Array<string>>();
  prior.forEach((asset) => {
    const name = cleanText(asset.name);
    if (!name) {
      return;
    }
    priorNameByIdentity.set(`id:${String(asset.id)}`, name);
    const stableKey = cleanText(asset.stableKey);
    if (stableKey) {
      priorNameByIdentity.set(`key:${stableKey}`, name);
    }
    priorKeysByName.set(name.toLowerCase(), [
      ...(priorKeysByName.get(name.toLowerCase()) ?? []),
      stableKey,
    ]);
  });
  const nextNameByKey = new Map(
    next.map((asset) => [cleanText(asset.stableKey), cleanText(asset.name)]),
  );
  return next.flatMap((asset): Array<AssetRename> => {
    const id = String(asset.id);
    const stableKey = cleanText(asset.stableKey) || undefined;
    const before =
      priorNameByIdentity.get(`id:${id}`) ??
      (stableKey ? priorNameByIdentity.get(`key:${stableKey}`) : undefined);
    const after = cleanText(asset.name);
    if (!before || !after || before === after) {
      return [];
    }
    const rename = { id, ...(stableKey ? { stableKey } : {}), from: before, to: after };
    const group = priorKeysByName.get(before.toLowerCase()) ?? [];
    if (!stableKey || group.length < 2) {
      return [{ ...rename, mention: "follows" }];
    }
    // A version that kept the name keeps its mentions (owner decision A).
    const keeper = group.find(
      (key) =>
        key !== stableKey &&
        nextNameByKey.get(key)?.toLowerCase() === before.toLowerCase(),
    );
    if (keeper) {
      return [{ ...rename, mention: { keeper } }];
    }
    // Every version renamed apart: the text alone cannot choose which one it
    // meant, so it keeps the words it was written with.
    const newNames = new Set(
      group.map((key) => (nextNameByKey.get(key) ?? "").toLowerCase()),
    );
    return [{ ...rename, mention: newNames.size > 1 ? "stranded" : "follows" }];
  });
}
